import React, { useCallback, useMemo } from 'react';
import { Field, FormErrors, formValueSelector, reduxForm } from 'redux-form';
import i18n from '../../../i18n';
import { Box, Grid, Hidden } from '@material-ui/core';

import { useFormError } from 'core/form/form';

import { FormErrorMessage } from 'components/FormErrorMessage/FormErrorMessage';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { SwitchControlField } from 'components/SwitchControl/SwitchControl';
import { TextControlField } from 'components/TextControl/TextControl';

import { Form } from 'forms/types';
import { connect, useSelector } from 'react-redux';
import {
    registrationSettingsPaymentOnlineSelector,
    registrationSettingsYandexCheckoutSelector,
} from 'state/registration/selector';
import { RegistrationSlice } from 'state/registration/reducer';
import mergeWith from 'lodash/mergeWith';
import { UserMenuSlice } from 'state/userMenu/reducer';
import { Text, TextTypography } from 'components/Text/Text';
import { RoutePath } from 'RoutePath';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import { YandexCheckoutApplicationStatus } from 'types';
import { useAction } from 'core/store/store';
import { makeRegistrationSettingsYandexCheckoutDeleteAction } from 'state/registration/actions';
import { DialogNew } from '../../components/Dialog/DialogNew';
import { isUIEnabledSelector } from '../../state/app/selectors';

const DEFAULT_YANDEX_CHECKOUT_FORM_DATA = {
    email: '',
    inn: '',
    status: YandexCheckoutApplicationStatus.NONE,
};

export interface YandexCheckoutSetupFormInterface {
    inn: string;
    email: string;
    status: YandexCheckoutApplicationStatus;
}

const validate = (values: YandexCheckoutSetupFormInterface) => {
    const errors: FormErrors<YandexCheckoutSetupFormInterface, string> = {
        inn: null,
        email: null,
        status: YandexCheckoutApplicationStatus.NONE,
    };
    if (values && values.status !== YandexCheckoutApplicationStatus.PROCESSED) {
        if ((values.inn || '').length === 0) {
            errors.inn = 'common:validation.required';
        } else if (values.inn.length < 10 || (values.inn.length > 10 && values.inn.length !== 12)) {
            errors.inn = 'onboarding:settings.yandexCheckoutSetupCard.errors.innLength';
        }
        if ((values.email || '').length === 0) {
            errors.email = 'common:validation.required';
        } else if (!/^.+@.+\..+$/i.test(values.email)) {
            errors.email = 'onboarding:settings.yandexCheckoutSetupCard.errors.emailValidation';
        }
    }
    return errors;
};

let YandexCheckoutSetupFormConnected: any = reduxForm({
    destroyOnUnmount: false,
    validate,
    form: Form.YANDEX_CHECKOUT_SETUP_FORM,
    enableReinitialize: true,
})((props: any) => {
    const { handleSubmit, onClose, initialize } = props;
    const { t } = i18n.useTranslation();
    const action = useAction();
    const error = useFormError(Form.YANDEX_CHECKOUT_SETUP_FORM);

    const yandexCheckoutData = useSelector(registrationSettingsYandexCheckoutSelector);
    const paymentOnlineData = useSelector(registrationSettingsPaymentOnlineSelector);
    const handleClose = useCallback(() => {
        if (yandexCheckoutData.status === YandexCheckoutApplicationStatus.NONE) {
            initialize(DEFAULT_YANDEX_CHECKOUT_FORM_DATA);
        } else {
            initialize(yandexCheckoutData);
        }
        onClose();
    }, [yandexCheckoutData, initialize, onClose]);

    const handleDeleteRequest = useCallback(() => {
        action(makeRegistrationSettingsYandexCheckoutDeleteAction);
    }, []);

    const yandexCheckoutStatus = useMemo(() => yandexCheckoutData.status || YandexCheckoutApplicationStatus.NONE, [
        yandexCheckoutData.status,
    ]);
    const yandexCheckoutStatusTranslation = useMemo(
        () => t(`common:enums.YANDEX_CHECKOUT_APP_STATUS.${yandexCheckoutStatus}`),
        [yandexCheckoutStatus],
    );
    const isUIEnabled = useSelector(isUIEnabledSelector);

    return (
        <DialogNew
            title={t('onboarding:settings.yandexCheckoutSetup')}
            width={423}
            onClose={handleClose}
            onSubmit={handleSubmit}
            leftButton={t('common:settings.closeButton')}
            rightButton={t(
                `onboarding:settings.yandexCheckoutSetupCard.request.${
                    yandexCheckoutStatus === YandexCheckoutApplicationStatus.NONE ? 'create' : 'update'
                }`,
            )}
            isUIEnabled={isUIEnabled}
        >
            <form onSubmit={handleSubmit}>
                {yandexCheckoutStatus !== YandexCheckoutApplicationStatus.PROCESSED ? (
                    <Box>
                        <Box mb={2}>
                            <Text typography={TextTypography.BODY}>
                                {t('onboarding:settings.yandexCheckoutSetupCard.request.info')}
                                <a href={RoutePath.PAYMENTS} target="_blank">
                                    {t('feed:store:payment:title')}
                                </a>
                            </Text>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Field
                                    name="email"
                                    component={TextControlField}
                                    label={t('onboarding:settings.yandexCheckoutSetupCard.email')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Field
                                    name="inn"
                                    component={TextControlField}
                                    label={t('onboarding:settings.yandexCheckoutSetupCard.inn')}
                                />
                            </Grid>
                            {error && (
                                <Grid item xs={12}>
                                    <FormErrorMessage error={error} />
                                </Grid>
                            )}
                        </Grid>
                        <Box mt={2} mb={2}>
                            <Text typography={TextTypography.PARA_STRONG}>
                                {t('onboarding:settings.yandexCheckoutSetupCard.request.status')}:
                            </Text>
                            <Text typography={TextTypography.PARA}>{yandexCheckoutStatusTranslation}</Text>
                        </Box>

                        <Grid container spacing={3}>
                            {yandexCheckoutStatus !== YandexCheckoutApplicationStatus.NONE && (
                                <>
                                    <Grid item xs={12}>
                                        <HorizontalDivider />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <LargeButton color={LargeButtonColor.ERROR} onClick={handleDeleteRequest}>
                                            {t('onboarding:settings.yandexCheckoutSetupCard.request.delete')}
                                        </LargeButton>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                ) : (
                    <>
                        <Hidden smUp>
                            <Box height={16} />
                        </Hidden>

                        <Box m={2} mb={3}>
                            <Box mb={2}>
                                <Text typography={TextTypography.BODY}>
                                    {t('onboarding:settings.yandexCheckoutSetupCard.yooKassa.info')}
                                </Text>
                            </Box>
                            <Grid item xs={12}>
                                <Field
                                    name="isEnabled"
                                    component={SwitchControlField}
                                    label={t(`onboarding:settings.yandexCheckoutSetupCard.yooKassa.title`)}
                                />
                            </Grid>
                            {paymentOnlineData?.enabled && (
                                <Box mt={1}>
                                    <Text typography={TextTypography.PARA}>
                                        {t('onboarding:settings.yandexCheckoutSetupCard.yooKassa.shopId', {
                                            shopId: paymentOnlineData?.shopId,
                                        })}
                                    </Text>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </form>
        </DialogNew>
    );
});

const selector = formValueSelector(Form.YANDEX_CHECKOUT_SETUP_FORM);

YandexCheckoutSetupFormConnected = connect((state: RegistrationSlice | UserMenuSlice) => {
    const { inn, email } = selector(state, 'inn', 'email');
    const paymentOnlineData = (state as RegistrationSlice).registration.settings.paymentOnline;
    const initialValues = mergeWith(
        {},
        DEFAULT_YANDEX_CHECKOUT_FORM_DATA,
        { email: (state as UserMenuSlice).userMenu.profile.email },
        (state as RegistrationSlice).registration.settings.yandexCheckout,
        { isEnabled: paymentOnlineData?.enabled, shopId: paymentOnlineData?.shopId },
        (o, s) => (s === null ? o : s),
    );
    return { inn, email, initialValues: initialValues };
})(YandexCheckoutSetupFormConnected);

export const YandexCheckoutSetupForm = YandexCheckoutSetupFormConnected;
